import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserRole } from '@hooddesk/shared-types';

export interface AuthUser {
  id: string;
  society_id: string | null;
  role: UserRole;
  phone?: string;
  email?: string;
  full_name?: string;
}

/**
 * When a super-admin "opens as admin" we hold a snapshot of the ORIGINAL
 * super-admin session so we can EXIT impersonation client-side without
 * another login round-trip. The top-level token/user fields are overwritten
 * with the tenant-scoped values from the impersonate API while this block
 * stays pinned.
 */
export interface ImpersonationContext {
  impersonating_society_id: string;
  impersonating_society_name: string;
  super_admin_accessToken: string;
  super_admin_refreshToken: string;
  super_admin_expiresAt: number;
  super_admin_user: {
    id: string;
    society_id: string | null;
    role: 'super_admin';
    email?: string;
    full_name?: string;
  };
}

/**
 * Response shape from `POST /v1/superadmin/societies/:id/impersonate`.
 * Includes a fresh tenant-scoped admin JWT plus an `impersonation` block
 * carrying just enough metadata to render the warning banner and audit
 * downstream calls.
 */
export interface ImpersonatePayload {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type?: 'Bearer';
  user: AuthUser;
  society: { id: string; name: string };
  impersonation: {
    impersonating_society_id: string;
    impersonating_society_name: string;
    super_admin_user_id: string;
  };
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // epoch ms
  user: AuthUser | null;
  impersonation: ImpersonationContext | null;
  setSession: (args: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: AuthUser | null;
  }) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
  /**
   * Snapshot the current super-admin session, then overwrite top-level
   * token + user fields with the tenant-scoped values from the impersonate
   * response. No-op (returns false) if the current user is not a super_admin.
   */
  startImpersonation: (payload: ImpersonatePayload) => boolean;
  /**
   * Restore the snapshotted super-admin session and clear the impersonation
   * block. Returns false if no snapshot exists (callers should fall back to
   * a normal logout in that case).
   */
  exitImpersonation: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      impersonation: null,
      setSession: ({ accessToken, refreshToken, expiresIn, user }) => {
        set({
          accessToken,
          refreshToken,
          expiresAt: Date.now() + expiresIn * 1000,
          user
        });
      },
      clear: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          impersonation: null
        }),
      isAuthenticated: () => {
        const { accessToken, expiresAt } = get();
        if (!accessToken) return false;
        // 30s grace
        return !expiresAt || Date.now() < expiresAt - 30_000;
      },
      startImpersonation: (payload) => {
        const current = get();
        if (
          !current.accessToken ||
          !current.refreshToken ||
          !current.expiresAt ||
          !current.user ||
          current.user.role !== 'super_admin'
        ) {
          // Refuse to start impersonation if we don't have a super-admin
          // session to snapshot. Caller should treat this as an error.
          return false;
        }
        const snapshot: ImpersonationContext = {
          impersonating_society_id: payload.impersonation.impersonating_society_id,
          impersonating_society_name: payload.impersonation.impersonating_society_name,
          super_admin_accessToken: current.accessToken,
          super_admin_refreshToken: current.refreshToken,
          super_admin_expiresAt: current.expiresAt,
          super_admin_user: {
            id: current.user.id,
            society_id: current.user.society_id,
            role: 'super_admin',
            email: current.user.email,
            full_name: current.user.full_name
          }
        };
        set({
          accessToken: payload.access_token,
          refreshToken: payload.refresh_token,
          expiresAt: Date.now() + payload.expires_in * 1000,
          user: payload.user,
          impersonation: snapshot
        });
        return true;
      },
      exitImpersonation: () => {
        const { impersonation } = get();
        if (!impersonation) {
          get().clear();
          return false;
        }
        set({
          accessToken: impersonation.super_admin_accessToken,
          refreshToken: impersonation.super_admin_refreshToken,
          expiresAt: impersonation.super_admin_expiresAt,
          user: impersonation.super_admin_user,
          impersonation: null
        });
        return true;
      }
    }),
    {
      name: 'vt-auth'
    }
  )
);
